import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subject, takeUntil } from 'rxjs';

import { APIError, HighlightsAPIService } from 'shared/services';
import { Highlight } from 'shared/models';
import { ToastService } from 'shared/ui';

import { USER_HIGHLIGHT_STYLES } from '../contents/contents.component';
import { AnnotationListComponent } from '../annotation-list/annotation-list.component';
import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { EventBusService } from '../../services/event-bus.service';
import { UIStateService } from '../../services/ui-state.service';

const USER_HIGHLIGHT_STYLE_MAP = USER_HIGHLIGHT_STYLES.reduce((map, style) => {
  map[style.styleClass] = style.style;
  return map;
}, {} as { [styleClass: string]: string });

@Component({
  selector: 'viewer-highlight-list',
  templateUrl: './highlight-list.component.html',
  styleUrls: [
    '../annotation-list/annotation-list.component.scss',
    './highlight-list.component.scss',
  ],
})
export class HighlightListComponent
  extends AnnotationListComponent<Highlight>
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('listContainer', { static: false })
  listContainer?: ElementRef;

  private _unsubscriber = new Subject<void>();
  private _scrollTimeout?: any; // 스크롤 타이머 추가

  public readonly _styleClassStyleMap = USER_HIGHLIGHT_STYLE_MAP;

  public _selectedFilter: string | null = null;

  public __items: AnnotationListComponent<Highlight>['_items'] = [];

  private _bid?: string;

  public _isCommentaryEditorMode = false;

  constructor(
    private _clipboard: Clipboard,
    private _highlightsAPIService: HighlightsAPIService,
    private _toastService: ToastService,
    private _annotationsService: AnnotationsV2Service,
    private _eventBusService: EventBusService,
    private _uiStateService: UIStateService
  ) {
    super();
  }

  override ngOnInit(): void {
    this._isCommentaryEditorMode = this._uiStateService.isCommentaryEditor;

    this._eventBusService
      .on('ContentsComponent:highlightUpdated')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this.loadItems();
      });

    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    if (this.listContainer) {
      this.listContainer.nativeElement.addEventListener('scroll', () => {
        this._closeAllMenus();

        // 스크롤 중일 때 클래스 추가
        this.listContainer!.nativeElement.classList.add('is-scrolling');

        // 기존 타이머가 있으면 제거
        if (this._scrollTimeout) {
          clearTimeout(this._scrollTimeout);
        }

        // 스크롤이 멈춘 후 300ms 후에 클래스 제거
        this._scrollTimeout = setTimeout(() => {
          this.listContainer!.nativeElement.classList.remove('is-scrolling');
        }, 300);
      });
    }
  }

  ngOnDestroy(): void {
    // 타이머 정리
    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
    }

    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  @Input()
  set bid(bid: string | undefined) {
    this._bid = bid;
    this.loadItems();
  }

  get bid(): string | undefined {
    return this._bid;
  }

  override loadItems(): void {
    if (!this.bid) {
      return;
    }

    this._highlightsAPIService
      .get(
        this.bid,
        0,
        999,
        this._isCommentaryEditorMode
          ? {
              commentary_id: this._annotationsService.getState().commentaryId,
              filter: 'theirs',
            }
          : {
              group_id: this._annotationsService.getState().groupId,
              filter: 'mine',
            }
      )
      .subscribe((highlights) => {
        this.__items = highlights.map((highlight) => ({
          ...highlight,
          selected: false,
          updatedDateTime: new Date(highlight.created_at).getTime(),
        }));
        this._applyFilter();
      });
  }

  _onCopyMenuClick(item: Highlight): void {
    this._clipboard.copy(item.text);

    this._toastService.open('하이라이트가 복사되었습니다.');
  }

  _onDeleteMenuClick(item: Highlight): void {
    this._deleteItems([item.id]);
  }

  _onCopySelectedItemsButtonClick(): void {
    this._clipboard.copy(
      this._getSelectedItems()
        .map((item) => item.text)
        .join('\n\n')
    );

    this._toastService.open('하이라이트가 복사되었습니다.');
  }

  _onDeleteSelectedItemsButtonClick(): void {
    const ids = this._getSelectedItems().map((item) => item.id);
    this._deleteItems(ids);
  }

  _onFilterChange(): void {
    this._applyFilter();
  }

  _applyFilter(): void {
    this._items = !this._selectedFilter
      ? this.__items
      : this.__items.filter((item) => item.style === this._selectedFilter);
    this._sortItems();
  }

  _onItemClick(item: Highlight): void {
    this.itemClicked.emit(item.url);
  }

  private _deleteItems(ids: string[]): void {
    if (
      this._uiStateService.isCommentaryEditor &&
      !this._uiStateService.canEditCommentary
    ) {
      this._toastService.openWarning(
        '판매 설정이 완료된 코멘터리는 수정이 불가합니다.'
      );

      return;
    }

    this._annotationsService.deleteHighlightsById(ids).subscribe({
      next: () => {
        this.__items = this.__items.filter(
          (item) => ids.indexOf(item.id) === -1
        );
        this._applyFilter();
        this._toastService.open('하이라이트가 삭제되었습니다.');
      },
      error: (error: APIError) => {
        this._toastService.open(error.message);
      },
    });
  }

  // 모든 열린 메뉴를 닫는 메서드
  private _closeAllMenus(): void {
    const backdropClickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: 0,
      clientY: 0,
    });

    // document.body에 클릭 이벤트를 발생시켜 CDK의 backdrop 클릭 처리 로직을 트리거
    document.body.dispatchEvent(backdropClickEvent);

    // 또는 더 직접적으로 overlay backdrop을 찾아서 클릭 이벤트 발생
    const overlayBackdrop = document.querySelector('.cdk-overlay-backdrop');
    if (overlayBackdrop) {
      overlayBackdrop.dispatchEvent(backdropClickEvent);
    }
  }
}
